import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup 
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch, 
  FaTag, FaPercentage, FaDollarSign 
} from 'react-icons/fa';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AddEditCoupon from './AddEditCoupon';

const CouponList = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCoupons = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/coupons`, {
        headers: { 'x-auth-token': token },
        params: { 
          page,
          search: searchTerm 
        }
      });
      
      setCoupons(response.data.coupons);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError(err.response?.data?.error || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [searchTerm]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/coupons/${couponToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });
      
      setShowDeleteModal(false);
      fetchCoupons(currentPage);
    } catch (err) {
      console.error('Error deleting coupon:', err);
      setError(err.response?.data?.error || 'Failed to delete coupon');
    }
  };

  const getTypeBadge = (type) => {
    return type === 'percentage' ? (
      <Badge bg="info">
        <FaPercentage className="me-1" /> Percentage
      </Badge>
    ) : (
      <Badge bg="success">
        <FaDollarSign className="me-1" /> Fixed Amount
      </Badge>
    );
  };

  const getStatusBadge = (isUsed) => {
    return isUsed ? (
      <Badge bg="secondary">Used</Badge>
    ) : (
      <Badge bg="primary">Active</Badge>
    );
  };

  return (
    <div className="coupons-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Coupon Management</h2>
        <Link to="/admin/coupons/add" className="btn btn-primary">
          <FaPlus className="me-2" />
          Add New Coupon
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search coupons by code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.length > 0 ? (
                coupons.map((coupon) => (
                  <tr key={coupon._id}>
                    <td>
                      <FaTag className="me-2" />
                      <strong>{coupon.code}</strong>
                    </td>
                    <td>{coupon.discount}{coupon.type === 'percentage' ? '%' : ''}</td>
                    <td>{getTypeBadge(coupon.type)}</td>
                    <td>{getStatusBadge(coupon.isUsed)}</td>
                    <td>{new Date(coupon.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Link 
                          to={`/admin/coupons/edit/${coupon._id}`}
                          className="btn btn-sm btn-primary"
                        >
                          <FaEdit />
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setCouponToDelete(coupon);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No coupons found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchCoupons(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <li 
                      key={page} 
                      className={`page-item ${currentPage === page ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link" 
                        onClick={() => fetchCoupons(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchCoupons(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete coupon <strong>{couponToDelete?.code}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Coupon
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CouponList;